import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MultiSelectFilter from '@/components/MultiSelectFilter';
import { RevenueSheet } from '@/components/RevenueSheet';
import { TargetsDialog } from '@/components/RevenueTargetsDialog';
import { toast } from 'sonner';
import { DownloadSimple, ArrowsClockwise, PencilSimple } from '@phosphor-icons/react';

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtLong = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// Landscape A4, crisp 3x render, pages cut only between table rows (never through text).
async function downloadPdf(src, fname) {
  const [{ toCanvas }, { jsPDF }] = await Promise.all([import('html-to-image'), import('jspdf')]);
  const SCALE = 3;
  // Render an unclipped copy (the on-screen sheet sits inside a horizontal scroller).
  const holder = document.createElement('div');
  holder.style.cssText = 'position:fixed;left:0;top:0;z-index:-1;background:#fff;';
  const node = src.cloneNode(true);
  holder.appendChild(node);
  document.body.appendChild(holder);
  try {
  const top = node.getBoundingClientRect().top;
  const cuts = [...node.querySelectorAll('tr:not([data-nobreak]), table')].map(el => Math.round((el.getBoundingClientRect().bottom - top) * SCALE));
  const canvas = await toCanvas(node, { pixelRatio: SCALE, backgroundColor: '#ffffff', cacheBust: true });
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4', compress: true });
  const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight(), m = 20;
  const ratio = (pw - m * 2) / canvas.width;
  const pageH = Math.floor((ph - m * 2) / ratio);
  const breaks = [...new Set(cuts)].filter(y => y > 0 && y <= canvas.height).sort((a, b) => a - b);
  let y = 0, first = true;
  while (y < canvas.height - 2) {
    let end = Math.min(y + pageH, canvas.height);
    if (end < canvas.height) {
      const fit = breaks.filter(b => b > y + 50 && b <= end);
      if (fit.length) end = fit[fit.length - 1];
    }
    const slice = document.createElement('canvas');
    slice.width = canvas.width; slice.height = end - y;
    const ctx = slice.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, y, canvas.width, end - y, 0, 0, canvas.width, end - y);
    if (!first) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/png'), 'PNG', m, m, pw - m * 2, (end - y) * ratio, undefined, 'FAST');
    first = false; y = end;
  }
  pdf.save(fname);
  } finally { holder.remove(); }
}

export default function DailyClinicRevenuePage() {
  const [month, setMonth] = useState(thisMonth());
  const [asOf, setAsOf] = useState('');
  const [clusters, setClusters] = useState([]);
  const [catchments, setCatchments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const sheetRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/reports/daily-clinic-revenue', { params: { month, as_of: asOf, clusters: clusters.join(','), catchments: catchments.join(',') } });
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [month, asOf, clusters, catchments]);
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = data ? `Daily Clinic Revenue — MTD ${fmtLong(data.start_date)} to ${fmtLong(data.end_date)} (excl. GST)` : '';
  const exportPdf = async () => {
    setPdfBusy(true);
    try { await downloadPdf(sheetRef.current, `Daily_Clinic_Revenue_${data.end_date}.pdf`); }
    catch { toast.error('PDF generation failed'); }
    finally { setPdfBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="daily-clinic-revenue-page">
      <PageHeader title="Daily Clinic Revenue" subtitle="Month-to-date revenue, bookings & credit notes vs targets — clinic, cluster and lead-source wise (amounts without GST)" />
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div><label className="text-[10px] text-[#64748B] block mb-1">MONTH (MTD)</label>
          <Input type="month" value={month} max={thisMonth()} onChange={e => { setMonth(e.target.value || thisMonth()); setAsOf(''); }} className="h-8 text-xs w-40" data-testid="dcr-month" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">UP TO DATE</label>
          <Input type="date" value={asOf} min={`${month}-01`} max={data?.end_date} onChange={e => setAsOf(e.target.value)} className="h-8 text-xs w-36" data-testid="dcr-asof" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">CLUSTER</label>
          <MultiSelectFilter value={clusters} onChange={setClusters} options={data?.filter_options?.clusters || []} allLabel="All Clusters" noun="clusters" testid="dcr-clusters" buttonClassName="h-8 text-xs w-56" /></div>
        <div><label className="text-[10px] text-[#64748B] block mb-1">CATCHMENT</label>
          <MultiSelectFilter value={catchments} onChange={setCatchments} options={(data?.filter_options?.catchments || []).map(c => ({ value: c, label: c }))} allLabel="All Catchments" noun="catchments" testid="dcr-catchments" buttonClassName="h-8 text-xs w-48" /></div>
        <Button size="sm" onClick={load} disabled={loading} className="h-8 text-xs bg-[#0F4C81] text-white" data-testid="dcr-apply"><ArrowsClockwise size={12} className="mr-1" /> {loading ? 'Loading…' : 'Apply'}</Button>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="h-8 text-xs" data-testid="dcr-edit-targets"><PencilSimple size={12} className="mr-1" /> Targets & Heads</Button>
        <Button size="sm" variant="outline" onClick={exportPdf} disabled={!data || pdfBusy} className="h-8 text-xs" data-testid="dcr-pdf"><DownloadSimple size={12} className="mr-1" /> {pdfBusy ? 'Preparing…' : 'Download PDF'}</Button>
      </div>
      {data && !data.has_month_config && <p className="text-[11px] text-amber-700 px-1" data-testid="dcr-config-note">No targets saved for {month} yet — showing the last saved (or default) targets. Use "Targets & Heads" to set this month's targets.</p>}
      <div className="overflow-x-auto bg-white border border-[#E2E8F0] rounded-xl">
        {data && <div ref={sheetRef} className="inline-block"><RevenueSheet data={data} title={title} /></div>}
      </div>
      {editing && <TargetsDialog month={month} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}
